import { useEffect, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, money } from "@/lib/api";
import { Card, PageHeader } from "@/components/admin/ui";

const RANGES = [[7, "Last 7 days"], [30, "Last 30 days"], [90, "Last 90 days"], [365, "Last 12 months"]];

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics", { params: { days } }).then((r) => setD(r.data));
  }, [days]);

  return (
    <div data-testid="admin-analytics-page">
      <PageHeader title="Analytics" subtitle="Real sales from paid orders only.">
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-9 px-3 rounded-lg border border-zinc-300 bg-white text-sm" data-testid="analytics-range-select">
          {RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </PageHeader>
      {d && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[["Total sales", money(d.total_sales), "total-sales"], ["Orders", d.orders, "orders"], ["Average order", money(d.average_order), "average-order"], ["Items sold", d.units_sold, "units-sold"]].map(([l, v, t]) => (
              <div key={l} className="admin-card p-5" data-testid={`analytics-${t}`}>
                <p className="text-[13px] text-zinc-500">{l}</p>
                <p className="text-2xl font-bold mt-1">{v}</p>
              </div>
            ))}
          </div>
          <Card title="Sales over time" className="mt-4" testid="analytics-chart">
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={d.by_day} margin={{ left: -10, right: 10, top: 10 }}>
                  <defs>
                    <linearGradient id="sales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#09090b" stopOpacity={0.18} />
                      <stop offset="100%" stopColor="#09090b" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#f1f1f2" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#71717a" }} tickFormatter={(v) => v.slice(5)} minTickGap={24} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#71717a" }} tickFormatter={(v) => `$${v}`} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v) => money(v)} labelStyle={{ fontSize: 12 }} contentStyle={{ borderRadius: 8, border: "1px solid #e4e4e7", fontSize: 12 }} />
                  <Area type="monotone" dataKey="sales" stroke="#09090b" strokeWidth={2} fill="url(#sales)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card title="Top products" className="mt-4" testid="analytics-top-products">
            {d.top_products.length === 0 ? (
              <p className="text-sm text-zinc-500">No sales in this period yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs text-zinc-500"><tr><th className="text-left font-medium py-2">Product</th><th className="text-right font-medium py-2">Sold</th><th className="text-right font-medium py-2">Revenue</th></tr></thead>
                <tbody className="divide-y divide-zinc-100">
                  {d.top_products.map((t) => <tr key={t.name}><td className="py-2.5">{t.name}</td><td className="py-2.5 text-right">{t.units}</td><td className="py-2.5 text-right">{money(t.revenue)}</td></tr>)}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
